import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import {
  agentSettingsQuery,
  saveAgentFunctions,
  saveAgentScanning,
  type AgentSettingsTab,
  type DeviceAgentSettingsPage,
} from '../api/devices'
import { toast } from '../ui/notify'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import { DeviceAgentDeviceSettings } from './DeviceAgentDeviceSettings'
import { DeviceConnectionQualityCard } from './DeviceConnectionQualityCard'
import { DeviceAgentDiscovery } from './DeviceAgentDiscovery'
import { DeviceAgentHealth } from './DeviceAgentHealth'
import { DeviceAgentLog } from './DeviceAgentLog'
import { DeviceAgentOperations } from './DeviceAgentOperations'
import { DeviceDomainDeploy } from './DeviceDomainDeploy'
import { NotYetMoved, tabFallbackHref } from './bridge'
import type { ScreenProps } from './screens'

/**
 * Fila Agent Settings - overview/devices/others/agentSettings.php: banda
 * line-tabs, prima fila se deschide singura, insigna tab-badge. Functions
 * e agentFunctions.php (cele doua carti Network Monitoring / Configuration
 * Relay). Health e agentResourceConsuming.php. Agent Log e agentLog.php.
 * Settings e deviceSettings.php + agentAppearance.php + agentAssignment.php.
 * Discovery e banda din discoverySettings.php plus listele (Scan / Push now /
 * Users). Operations e device_scan.php (Run Scan inca deschide vechiul capat).
 *
 * Modules (deviceModuleSettings.php) NU mai e aici: de pe 24 septembrie 2026 e
 * fila ei in meniul fisei, imediat dupa Agent Settings, cu acelasi drept
 * (`overview.live_devices_settings_modules.read`) - vezi WorkspaceMenu::ITEMS
 * si `overview/deviceModulesSettings/` in screens.tsx.
 */

const AF_CSS = `.af-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:16px;margin:8px 0 20px}
@media(max-width:900px){.af-grid{grid-template-columns:1fr}}
.af-card{background:#fff;border:1px solid #e7ecf1;border-radius:10px;padding:20px 22px 18px;box-shadow:0 8px 20px rgba(0,0,0,.04);min-height:210px;display:flex;flex-direction:column}
.af-card.on{border-color:#b7e0c7}
.af-icon{width:42px;height:42px;border-radius:10px;display:flex;align-items:center;justify-content:center;color:#fff;font-size:18px;margin-bottom:12px}
.af-icon.watch{background:linear-gradient(135deg,#3598dc 0%,#2a6fad 100%)}
.af-icon.relay{background:linear-gradient(135deg,#8e44ad 0%,#6c3483 100%)}
.af-card h4{margin:0 0 8px;font-size:16px;color:#2c3e50}
.af-card p{margin:0 0 16px;font-size:13px;color:#5d6d7e;line-height:1.45;flex:1}
.af-foot{display:flex;align-items:center;justify-content:space-between;gap:12px}
.af-state{font-size:12px;font-weight:600;letter-spacing:.04em;text-transform:uppercase}
.af-state.off{color:#95a5a6}.af-state.on{color:#1b7a42}
.af-switch{position:relative;width:46px;height:26px;flex-shrink:0}
.af-switch input{opacity:0;width:0;height:0}
.af-switch span{position:absolute;inset:0;background:#c5ced6;border-radius:26px;cursor:pointer;transition:.2s}
.af-switch span:before{content:'';position:absolute;height:20px;width:20px;left:3px;top:3px;background:#fff;border-radius:50%;transition:.2s;box-shadow:0 1px 3px rgba(0,0,0,.2)}
.af-switch input:checked+span{background:#26a65b}
.af-switch input:checked+span:before{transform:translateX(20px)}
    .af-note{font-size:12px;color:#7f8c8d;margin:0 0 8px}
.af-grid-one{grid-template-columns:minmax(0,1fr)}
.af-card-wide{min-height:0}
.af-icon.scan{background:linear-gradient(135deg,#1a73e8 0%,#1552a8 100%)}
.af-scan{margin-top:16px;padding-top:16px;border-top:1px solid #eef1f5}
.af-scan[hidden]{display:none!important}
.af-row{display:flex;gap:12px;margin:0 0 12px;font-weight:400}
.af-k{flex:0 0 130px;padding-top:6px;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#a3aeb9}
.af-v{flex:1 1 auto;display:flex;flex-wrap:wrap;align-items:center;gap:8px;min-width:0}
.af-v .form-control{width:auto;min-width:120px}
.af-v .af-ldap{flex:1 1 320px}
.af-v [hidden],.af-range[hidden]{display:none!important}
.af-range{display:flex;gap:8px}
.af-check{display:inline-flex;align-items:center;gap:6px;margin:0;font-size:13px;color:#5d6d7e}
.af-hint{font-size:11px;color:#a3aeb9;flex:1 0 100%}
.af-save{padding-left:142px}
@media(max-width:700px){.af-row{flex-direction:column;gap:4px}.af-k{flex:0 0 auto;padding-top:0}.af-save{padding-left:0}}`

export function DeviceAgentSettings({ id, open, hasTab }: ScreenProps) {
  const { data, error } = useQuery(agentSettingsQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Settings page={data} open={open} hasTab={hasTab} />
}

function Settings({ page, open, hasTab }: { page: DeviceAgentSettingsPage; open?: (href: string) => void; hasTab?: (href: string) => boolean }) {
  const first = page.tabs[0]?.key ?? 'functions'
  const [active, setActive] = useState(first)
  const current = page.tabs.find((t) => t.key === active) ?? page.tabs[0]

  return (
    <div className="row">
      <div className="col-md-12">
        <ul className="nav nav-tabs line-tabs mb-2 agentSettingsTabs" role="tablist">
          {page.tabs.map((tab) => (
            <li key={tab.key} className={tab.key === active ? 'active' : undefined}>
              <a
                href={tabFallbackHref(page.device.id, tab.href)}
                onClick={(e) => {
                  e.preventDefault()
                  setActive(tab.key)
                }}
              >
                <i className={tab.icon}></i> {tab.label}{' '}
                <Badge tab={tab} />
              </a>
            </li>
          ))}
        </ul>
        <div className="tab-content agent-settings-pane">
          {current?.key === 'functions' ? (
            <Functions page={page} />
          ) : current?.key === 'health' ? (
            <DeviceAgentHealth id={page.device.id} />
          ) : current?.key === 'agent_log' ? (
            <DeviceAgentLog id={page.device.id} open={open} hasTab={hasTab} />
          ) : current?.key === 'settings' ? (
            <DeviceAgentDeviceSettings id={page.device.id} />
          ) : current?.key === 'discovery' ? (
            <DeviceAgentDiscovery id={page.device.id} />
          ) : current?.key === 'operations' ? (
            <DeviceAgentOperations id={page.device.id} />
          ) : current ? (
            <NotYetMoved id={page.device.id} href={current.href} label={current.label} />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Badge({ tab }: { tab: AgentSettingsTab }) {
  if (tab.badge === null || tab.badge === '') return null
  return (
    <span className={`tab-badge tab-badge-${tab.badgeTone}`} title={tab.badgeTitle}>
      {tab.badge}
    </span>
  )
}

function Functions({ page }: { page: DeviceAgentSettingsPage }) {
  const query = useQueryClient()
  const toggle = async (key: 'networkMonitor' | 'configRelay', on: boolean) => {
    await saveAgentFunctions(page.device.id, {
      networkMonitor: key === 'networkMonitor' ? on : page.functions.networkMonitor,
      configRelay: key === 'configRelay' ? on : page.functions.configRelay,
    })
    await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agent-settings'] })
  }

  return (
    <>
      <style>{AF_CSS}</style>
      <p className="af-note">
        These functions look at the network from <strong>inside</strong> this machine, on their own. Network Scanning
        finds what is not in the inventory yet; the other two work with devices that already are. A one-off scan does
        not need any of them - it lives on the <strong>Discovery</strong> tab, one button away.
      </p>
      <form id="agentFunctionsForm">
        <input type="hidden" name="device" value={page.device.id} />
        <div className="af-grid">
          <FlagCard
            flag="network_monitor"
            icon="watch"
            iconClass="fas fa-project-diagram"
            title="Network Monitoring"
            on={page.functions.networkMonitor}
            onToggle={(on) => void toggle('networkMonitor', on)}
          >
            This machine pings other registered devices — Managed or Unmanaged — from inside the LAN, and reports Up /
            Down on the <strong>Neighbor Watch</strong> tab in Proactivitee.
          </FlagCard>
          <FlagCard
            flag="config_relay"
            icon="relay"
            iconClass="fas fa-network-wired"
            title="Configuration Relay"
            on={page.functions.configRelay}
            onToggle={(on) => void toggle('configRelay', on)}
          >
            This machine opens SSH to switches, firewalls and other gear, using a user and password from{' '}
            <strong>Pass Safe</strong>. People use it from the Configuration Relay tab; the AI agent uses the same path.
          </FlagCard>
        </div>
      </form>
      {page.functions.domainController ? <DeviceDomainDeploy deviceId={page.device.id} /> : null}
      <Scanning page={page} />
      <DeviceConnectionQualityCard deviceId={page.device.id} />
    </>
  )
}

function Scanning({ page }: { page: DeviceAgentSettingsPage }) {
  const query = useQueryClient()
  const scan = page.scanning
  const [on, setOn] = useState(scan.on)
  const [recurrence, setRecurrence] = useState(scan.recurrence)
  const [interval, setInterval] = useState(scan.recurrence_interval)
  const [moment, setMoment] = useState(scan.recurrence_moment)
  const [time, setTime] = useState(scan.recurrence_time)
  const [filter, setFilter] = useState(scan.filter_type)
  const [ldap, setLdap] = useState(scan.custom_filter)
  const [ipFrom, setIpFrom] = useState(scan.ip_from)
  const [ipTo, setIpTo] = useState(scan.ip_to)
  const [user, setUser] = useState(scan.domain_user)
  const [password, setPassword] = useState(scan.hasPassword ? '********' : '')
  const [saving, setSaving] = useState(false)

  const fields = () => ({
    ad_relay: on ? 1 : 0,
    recurrence: recurrence ? 1 : 0,
    recurrence_interval: interval,
    recurrence_moment: moment,
    recurrence_time: time,
    filter_type: filter,
    custom_filter: ldap,
    custom_ip_1: ipFrom,
    custom_ip_2: ipTo,
    domain_username: user,
    domain_password: password,
  })

  const save = async (nextOn?: boolean) => {
    setSaving(true)
    try {
      const payload = fields()
      if (nextOn !== undefined) payload.ad_relay = nextOn ? 1 : 0
      await saveAgentScanning(page.device.id, payload)
      toast('Saved.', 'Network Scanning')
      await query.invalidateQueries({ queryKey: ['device', page.device.id, 'agent-settings'] })
    } catch {
      toast('It could not be saved.', 'Network Scanning', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="af-grid af-grid-one">
      <div className={`af-card af-card-wide${on ? ' on' : ''}`} data-flag="ad_relay">
        <div className="af-icon scan">
          <i className="fas fa-broadcast-tower"></i>
        </div>
        <h4>Network Scanning</h4>
        <p>
          This machine looks for what is on the network around it, on its own, and writes what it finds on the{' '}
          <strong>Discovery</strong> tab. Off, that tab still scans - but only when somebody presses <strong>Scan</strong>.
        </p>
        <div className="af-foot">
          <span className={`af-state ${on ? 'on' : 'off'}`}>{on ? 'On' : 'Off'}</span>
          <label className="af-switch">
            <input
              type="checkbox"
              name="ad_relay"
              value="1"
              form="agentScanForm"
              checked={on}
              onChange={(e) => {
                const next = e.target.checked
                setOn(next)
                void save(next)
              }}
            />
            <span></span>
          </label>
        </div>
        <form
          id="agentScanForm"
          className="af-scan"
          hidden={!on}
          onSubmit={(e) => {
            e.preventDefault()
            void save()
          }}
        >
          <input type="hidden" name="device_unique" value={page.device.id} />
          <label className="af-row">
            <span className="af-k">How often</span>
            <span className="af-v">
              <span className="af-check">
                <SettingsCheck name="recurrence" value="1" checked={recurrence} onChange={setRecurrence} label="on a schedule" />
              </span>
              <select name="recurrence_interval" className="form-control input-sm" value={interval} onChange={(e) => setInterval(Number(e.target.value))}>
                <option value={1}>every day</option>
                <option value={2}>every week</option>
                <option value={3}>every month</option>
              </select>
              <select name="recurrence_moment" className="form-control input-sm af-moment" hidden={interval !== 2} value={moment} onChange={(e) => setMoment(Number(e.target.value))}>
                {['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].slice(1).map((day, i) => (
                  <option key={day} value={i + 1}>
                    {day}
                  </option>
                ))}
              </select>
              <input
                type="text"
                className="form-control input-sm timepicker af-time"
                name="recurrence_time"
                placeholder="19:00"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </span>
          </label>
          <label className="af-row">
            <span className="af-k">Where to look</span>
            <span className="af-v">
              <select name="filter_type" className="form-control input-sm af-filter" value={filter} onChange={(e) => setFilter(Number(e.target.value))}>
                <option value={1}>the whole network</option>
                <option value={2}>an LDAP branch</option>
                <option value={3}>an IP range</option>
              </select>
              <input
                type="text"
                className="form-control input-sm af-ldap"
                name="custom_filter"
                placeholder="LDAP://server/OU=Computers,DC=example,DC=local"
                hidden={filter !== 2}
                value={ldap}
                onChange={(e) => setLdap(e.target.value)}
              />
              <span className="af-range" hidden={filter !== 3}>
                <input type="text" className="form-control input-sm" name="custom_ip_1" placeholder="from" value={ipFrom} onChange={(e) => setIpFrom(e.target.value)} />
                <input type="text" className="form-control input-sm" name="custom_ip_2" placeholder="to" value={ipTo} onChange={(e) => setIpTo(e.target.value)} />
              </span>
            </span>
          </label>
          <label className="af-row">
            <span className="af-k">Domain account</span>
            <span className="af-v">
              <input type="text" className="form-control input-sm" name="domain_username" placeholder="username" autoComplete="off" value={user} onChange={(e) => setUser(e.target.value)} />
              <input type="password" className="form-control input-sm" name="domain_password" placeholder="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <span className="af-hint">optional - only for reading a Windows domain</span>
            </span>
          </label>
          <div className="af-save">
            <button type="submit" className="btn btn-success btn-sm" disabled={saving}>
              <i className="fa fa-check"></i> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function FlagCard({
  flag,
  icon,
  iconClass,
  title,
  on,
  onToggle,
  children,
}: {
  flag: string
  icon: string
  iconClass: string
  title: string
  on: boolean
  onToggle(on: boolean): void
  children: ReactNode
}) {
  return (
    <div className={`af-card${on ? ' on' : ''}`} data-flag={flag}>
      <div className={`af-icon ${icon}`}>
        <i className={iconClass}></i>
      </div>
      <h4>{title}</h4>
      <p>{children}</p>
      <div className="af-foot">
        <span className={`af-state ${on ? 'on' : 'off'}`}>{on ? 'On' : 'Off'}</span>
        <label className="af-switch">
          <input type="checkbox" name={flag} value="1" checked={on} onChange={(e) => onToggle(e.target.checked)} />
          <span></span>
        </label>
      </div>
    </div>
  )
}
